import { useCallback, useState } from 'react';
import { cardSrc } from './resultParsers';

const CDN = 'https://wver.sprintstaticdata.com/v230/static/front/img';
const ITEM_W = 34.386;
const PER_VIEW = 8;

const SUIT_NAME = { SS: 'Spade', HH: 'Heart', CC: 'Club', DD: 'Diamond' };

function rankOf(code) {
  return String(code).slice(0, -2).toUpperCase();
}
function rankVal(r) {
  if (r === 'A') return 1;
  if (r === 'J') return 11;
  if (r === 'Q') return 12;
  if (r === 'K') return 13;
  return parseInt(r, 10) || 0;
}

// Owl result slider — functional paging (prev/next scroll). perView 8, itemW 34.386px.
function ResultOwl({ id, cards }) {
  const [page, setPage] = useState(0);
  const maxPage = Math.max(0, Math.ceil(cards.length / PER_VIEW) - 1);
  const cur = Math.min(page, maxPage);
  const atStart = cur <= 0;
  const atEnd = cur >= maxPage;
  const translateX = -(cur * PER_VIEW * ITEM_W);
  const stageWidth = cards.length * ITEM_W;
  const prev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
  const next = useCallback(() => setPage((p) => p + 1), []);

  return (
    <div id={id} className="ab-slider owl-carousel owl-theme owl-loaded owl-drag">
      <div className="owl-stage-outer">
        <div className="owl-stage" style={{ transform: `translate3d(${translateX}px, 0px, 0px)`, transition: 'all 0.25s ease', width: `${stageWidth}px` }}>
          {cards.map((code, i) => {
            const active = i >= cur * PER_VIEW && i < (cur + 1) * PER_VIEW;
            return (
              <div key={i} className={`owl-item${active ? ' active' : ''}`} style={{ width: `${ITEM_W}px` }}>
                <div className="casino-result-cards-item item"><img src={cardSrc(code)} alt={code} /></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={`owl-nav${cards.length <= PER_VIEW ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}><span aria-label="Previous">‹</span></button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}><span aria-label="Next">›</span></button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
}

export default function ABJResult({ detail }) {
  const allCards = (detail?.card || '').split(',').map((c) => c.trim()).filter(Boolean);
  const trumpCard = allCards[0] && allCards[0] !== '1' ? allCards[0] : null;
  const dealt = allCards.slice(1).filter((c) => c !== '1' && c !== '0');
  const aCards = dealt.filter((_, i) => i % 2 === 1);
  const bCards = dealt.filter((_, i) => i % 2 === 0);

  const aFirst = aCards[0];
  const aRest = aCards.slice(1);
  const bFirst = bCards[0];
  const bRest = bCards.slice(1);

  const winnat = detail?.winnat || '';
  const win = String(detail?.win ?? '');
  const isAWinner = winnat.toLowerCase() === 'andar' || win === '1';
  const isBWinner = winnat.toLowerCase() === 'bahar' || win === '2';
  const winnerText = isAWinner ? 'Andar' : isBWinner ? 'Bahar' : (winnat || '');

  // desc rdesc se seedhe: "<Winner>#<Suit>#<Odd/Even>#<Joker>" (e.g. "Bahar#Heart#Even#Q");
  // rdesc na ho to trump card se derive
  const rparts = String(detail?.rdesc || '').split('#');
  const suit = rparts[1] || (trumpCard ? (SUIT_NAME[String(trumpCard).slice(-2).toUpperCase()] || '') : '');
  const oddEven = rparts[2] || (trumpCard ? (rankVal(rankOf(trumpCard)) % 2 === 1 ? 'Odd' : 'Even') : '');
  const joker = rparts[3] || (trumpCard ? rankOf(trumpCard) : '');

  const winnerIcon = (
    <div className="casino-result-cards-item">
      <img src={`${CDN}/winner.png`} className="winner-icon" alt="winner" />
    </div>
  );

  return (
    <div className="row row5 ab2-result">
      <div className="col-12 col-lg-8">
        <div className="d-flex">
          <div className="col-1 abj-title">
            <div className="row row5"><div className="col-12"><b>A</b></div></div>
            <div className="row row5"><div className="col-12"><b>B</b></div></div>
          </div>
          <div className="abj-common-card">
            {trumpCard && (
              <div className="casino-result-cards-item"><img src={cardSrc(trumpCard)} alt={trumpCard} /></div>
            )}
          </div>
          <div className="col-9">
            {/* Andar row */}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-2">
                  {aFirst && <div className="casino-result-cards-item"><img src={cardSrc(aFirst)} alt={aFirst} /></div>}
                </div>
                <div className="col-8"><ResultOwl id="andarSlider" cards={aRest} /></div>
                <div className="col-2">{isAWinner ? winnerIcon : null}</div>
              </div>
            </div>
            {/* Bahar row */}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-2">
                  {bFirst && <div className="casino-result-cards-item"><img src={cardSrc(bFirst)} alt={bFirst} /></div>}
                </div>
                <div className="col-8"><ResultOwl id="baharSlider" cards={bRest} /></div>
                <div className="col-2">{isBWinner ? winnerIcon : null}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-4">
        <div className="casino-result-desc">
          <div className="casino-result-desc-item">
            <div>Winner <span className="float-right">:</span></div>
            <div>{winnerText}</div>
          </div>
          <div className="casino-result-desc-item">
            <div>Suit<span className="float-right">:</span></div>
            <div>{suit}</div>
          </div>
          <div className="casino-result-desc-item">
            <div>Odd/Even<span className="float-right">:</span></div>
            <div>{oddEven}</div>
          </div>
          <div className="casino-result-desc-item">
            <div>Joker<span className="float-right">:</span></div>
            <div>{joker}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
